import React, { useState } from 'react';
import { IoChevronBack, IoSearchOutline } from 'react-icons/io5';
import { BiRefresh } from 'react-icons/bi';
import { FiSave } from 'react-icons/fi';
import { FaUsers, FaShield, FaChevronDown } from 'react-icons/fa6';
import { HiOutlineDotsVertical } from 'react-icons/hi';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'Active' | 'Inactive';
  lastLogin: string;
  created: string;
}

interface Permission {
  name: string;
  enabled: boolean;
}

interface Role {
  name: string;
  description: string;
  permissions: Permission[];
}

const UserRoleManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'roles'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [roleFilter, setRoleFilter] = useState('All Roles');

  // Sample users data
  const [users] = useState<User[]>([
    {
      id: '1',
      name: 'Ruth Oyewusi',
      email: 'Ruth@gmail.com',
      role: 'Administrator',
      status: 'Active',
      lastLogin: '2024-01-15 14:30',
      created: '2023-06-15'
    },
    {
      id: '2',
      name: 'Ruth Oyewusi',
      email: 'Ruth@gmail.com',
      role: 'Operations Manager',
      status: 'Active',
      lastLogin: '2024-01-15 14:30',
      created: '2023-06-15'
    },
    {
      id: '3',
      name: 'Ruth Oyewusi',
      email: 'Ruth@gmail.com',
      role: 'Support Staff',
      status: 'Active',
      lastLogin: '2024-01-15 14:30',
      created: '2023-06-15'
    },
    {
      id: '4',
      name: 'Ruth Oyewusi',
      email: 'Ruth@gmail.com',
      role: 'Finance Staff',
      status: 'Inactive',
      lastLogin: '2024-01-15 14:30',
      created: '2023-06-15'
    },
    {
      id: '5',
      name: 'Ruth Oyewusi',
      email: 'Ruth@gmail.com',
      role: 'Operations Manager',
      status: 'Active',
      lastLogin: '2024-01-15 14:30',
      created: '2023-06-15'
    }
  ]);

  // Sample roles data with permissions
  const [roles, setRoles] = useState<Role[]>([
    {
      name: 'Administrator',
      description: 'Full system access and user management',
      permissions: [
        { name: 'Dashboard Access', enabled: true },
        { name: 'Rider Management', enabled: true },
        { name: 'Order Dispatch', enabled: true },
        { name: 'Real-time Tracking', enabled: true },
        { name: 'Performance Monitoring', enabled: true },
        { name: 'Issue Management', enabled: true },
        { name: 'Rider Appeals', enabled: true },
        { name: 'Earnings & Payouts', enabled: true },
        { name: 'Payout Requests', enabled: true },
        { name: 'Communication', enabled: true },
        { name: 'System Settings', enabled: true },
        { name: 'User Management', enabled: true }
      ]
    },
    {
      name: 'Operations Manager',
      description: 'Order management and rider operations',
      permissions: [
        { name: 'Dashboard Access', enabled: true },
        { name: 'Rider Management', enabled: true },
        { name: 'Order Dispatch', enabled: true },
        { name: 'Real-time Tracking', enabled: true },
        { name: 'Performance Monitoring', enabled: true },
        { name: 'Communication', enabled: true }
      ]
    },
    {
      name: 'Support Staff',
      description: 'Customer and rider support functions',
      permissions: [
        { name: 'Dashboard Access', enabled: true },
        { name: 'Rider Management', enabled: true },
        { name: 'Issue Management', enabled: true },
        { name: 'Communication', enabled: true }
      ]
    },
    {
      name: 'Finance Staff',
      description: 'Financial operations and payouts',
      permissions: [
        { name: 'Dashboard Access', enabled: true },
        { name: 'Earnings & Payouts', enabled: true },
        { name: 'Payout Requests', enabled: true }
      ]
    }
  ]);

  const handleSaveChanges = () => {
    console.log('Saving changes...', roles);
    // Here you would typically send the updated roles to your backend
  };

  const handleResetChanges = () => {
    console.log('Resetting changes...');
    // Here you would reset roles to their original state
  };

  const togglePermission = (roleIndex: number, permissionIndex: number) => {
    const updatedRoles = [...roles];
    updatedRoles[roleIndex].permissions[permissionIndex].enabled = 
      !updatedRoles[roleIndex].permissions[permissionIndex].enabled;
    setRoles(updatedRoles);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All Statuses' || user.status === statusFilter;
    const matchesRole = roleFilter === 'All Roles' || user.role === roleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button className="text-gray-400 hover:text-gray-600 transition-colors">
              <IoChevronBack className="w-5 h-5" />
            </button>
            <div>
              <div className="text-sm text-gray-500 mb-1">Logistics</div>
              <h1 className="text-2xl font-bold text-gray-900">User Role Management</h1>
              <p className="text-sm text-gray-600 mt-1">Configure delivery operations and system parameters</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleResetChanges}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <BiRefresh className="w-4 h-4" />
              <span>Reset Changes</span>
            </button>
            <button
              onClick={handleSaveChanges}
              className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <FiSave className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FaUsers className="w-4 h-4" />
            <span>Users</span>
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'roles'
                ? 'border-emerald-600 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FaShield className="w-4 h-4" />
            <span>Roles & Permission</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'users' ? (
          // Users Tab
          <div className="bg-white rounded-lg shadow-sm border">
            {/* Search and Filters */}
            <div className="p-6 border-b">
              <div className="flex items-center space-x-4">
                <div className="relative flex-1 max-w-md">
                  <IoSearchOutline className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search Users By name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option>All Statuses</option>
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                  <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option>All Roles</option>
                    <option>Administrator</option>
                    <option>Operations Manager</option>
                    <option>Support Staff</option>
                    <option>Finance Staff</option>
                  </select>
                  <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">Name</th>
                    <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">Role</th>
                    <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">Status</th>
                    <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">Last Log In</th>
                    <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">Created</th>
                    <th className="text-left py-3 px-6 font-medium text-gray-500 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredUsers.map((user, index) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div>
                          <div className="font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900">{user.role}</td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.status === 'Active'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900">{user.lastLogin}</td>
                      <td className="py-4 px-6 text-sm text-gray-900">{user.created}</td>
                      <td className="py-4 px-6">
                        <div className="relative">
                          <button className="text-gray-400 hover:text-gray-600">
                            <HiOutlineDotsVertical className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // Roles & Permissions Tab
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {roles.map((role, roleIndex) => (
              <div key={role.name} className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {role.permissions.map((permission, permissionIndex) => (
                      <div key={permission.name} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{permission.name}</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={permission.enabled}
                            onChange={() => togglePermission(roleIndex, permissionIndex)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserRoleManagement;